import { useEffect, useRef, useState } from 'react';

/**
 * CountUp
 * Animates a number from 0 → target when the element scrolls into view.
 *
 * Props:
 *   target   {number}  – final value
 *   suffix   {string}  – optional text appended after the number (e.g. "+")
 *   prefix   {string}  – optional text before the number (e.g. "Top")
 *   duration {number}  – animation duration in ms (default 1800)
 */
function CountUp({ target, suffix = '', prefix = '', duration = 1800 }) {
  const [count, setCount]     = useState(0);
  const [started, setStarted] = useState(false);
  const wrapRef               = useRef(null);

  // Trigger when wrapper enters the viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStarted(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    if (wrapRef.current) observer.observe(wrapRef.current);
    return () => observer.disconnect();
  }, []);

  // Run the count animation once triggered
  useEffect(() => {
    if (!started) return;
    const begin = performance.now();
    let raf;
    const step = (now) => {
      const t    = Math.min((now - begin) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3); // cubic ease-out
      setCount(Math.floor(ease * target));
      if (t < 1) {
        raf = requestAnimationFrame(step);
      } else {
        setCount(target);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [started, target, duration]);

  return (
    <span ref={wrapRef}>
      {prefix}{count}{suffix}
    </span>
  );
}

export default CountUp;
